export type PersonRole = "PRIMARY" | "SPOUSE" | "DEPENDANT";

export interface TaxSessionResponse {
  taxSessionId: number;
}

export interface PersonResponse {
  personId: number;
  role?: PersonRole;
}

export interface ApplicantPayload {
  [key: string]: string | boolean | number | undefined;

  role?: string;
}

export interface CraQuestionPayload {
  [key: string]: string | boolean | number | undefined;
}

export interface PersonCheckboxPayload {
  [key: string]: boolean | string | undefined;
  privacyPolicyAcknowledged?: boolean;
  privacyPolicyFullName?: string;
  privacyPolicyReadDate?: string;
}

export interface ContactInfoPayload {
  email: string;
  phone?: string;
}

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE || "http://localhost:8080"
).replace(/\/$/, "");

export async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export function getResponseError(
  status: number,
  body: unknown,
  fallback: string,
): string {
  if (typeof body === "string" && body.trim()) {
    return `${fallback}: ${status} ${body}`;
  }

  if (body && typeof body === "object") {
    const errorBody = body as {
      message?: string;
      detail?: string;
      error?: string;
    };

    const message = errorBody.message ?? errorBody.detail ?? errorBody.error;

    if (message) {
      return `${fallback}: ${status} ${message}`;
    }
  }

  return `${fallback}: server returned status ${status}`;
}

export function sanitizeEmptyFields<T extends Record<string, unknown>>(
  obj: T,
): T {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== "" && value !== null && value !== undefined) {
      sanitized[key] = value;
    }
  }

  return sanitized as T;
}

export function buildApplicantPayload(
  person: Record<string, unknown>,
  role: PersonRole,
): ApplicantPayload {
  return sanitizeEmptyFields({
    ...person,
    role,
  }) as ApplicantPayload;
}

export function buildCraQuestionPayload(
  person: Record<string, unknown>,
): CraQuestionPayload {
  const yesNoBooleanFields = new Set([
    "firstTimeFiler",
    "forProperty",
    "sellPrincipalResidence",
    "diplomat",
    "canadianCitizenship",
    "authorizeCra",
    "consentToShare",
    "acceptTerms",
  ]);

  const raw: Record<string, unknown> = {
    corrLanguage: person.corrLanguage,
    firstTimeFiler: person.firstTimeFiler,
    forProperty: person.forProperty,
    sellPrincipalResidence: person.sellPrincipalResidence,
    diplomat: person.diplomat,
    canadianCitizenship: person.canadianCitizenship,
    authorizeCra: person.authorizeCra,
    consentToShare: person.consentToShare,
    acceptTerms: person.acceptTerms,
    emailAddress: person.emailAddress,
    emailConfirmation: person.emailConfirmation,
  };

  const converted = Object.fromEntries(
    Object.entries(raw).map(([key, value]) => {
      if (yesNoBooleanFields.has(key)) {
        return [key, value === "Yes"];
      }

      return [key, value];
    }),
  );

  return sanitizeEmptyFields(converted) as CraQuestionPayload;
}

export function buildPersonCheckboxPayload(
  person: Record<string, unknown>,
): PersonCheckboxPayload {
  const checkboxState: PersonCheckboxPayload = {};

  for (const [field, value] of Object.entries(person)) {
    /*
     * Keep all Boolean checkbox fields,
     * including false values.
     */
    if (typeof value === "boolean") {
      checkboxState[field] = value;
    }
  }

  /*
   * Explicitly add privacy-policy
   * string/date fields.
   */
  const privacyPolicyFullName = String(
    person.privacyPolicyFullName ?? "",
  ).trim();

  const privacyPolicyReadDate = String(
    person.privacyPolicyReadDate ?? "",
  ).trim();

  checkboxState.privacyPolicyAcknowledged = Boolean(
    person.privacyPolicyAcknowledged,
  );

  checkboxState.privacyPolicyFullName = privacyPolicyFullName || undefined;

  checkboxState.privacyPolicyReadDate = privacyPolicyReadDate || undefined;

  return checkboxState;
}

export async function createTaxSession(): Promise<number> {
  const response = await fetch(`${API_BASE_URL}/api/tax-sessions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });

  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(response.status, body, "Tax session creation failed"),
    );
  }

  const session = body as TaxSessionResponse;

  if (!Number.isInteger(session?.taxSessionId) || session.taxSessionId <= 0) {
    console.error("Invalid tax-session response:", body);

    throw new Error("The backend did not return a valid taxSessionId");
  }

  return session.taxSessionId;
}

export async function createPerson(
  taxSessionId: number,
  role: PersonRole,
): Promise<PersonResponse> {
  if (!Number.isInteger(taxSessionId) || taxSessionId <= 0) {
    throw new Error(
      `Cannot create ${role}: invalid taxSessionId ${taxSessionId}`,
    );
  }

  const response = await fetch(
    `${API_BASE_URL}/api/persons/session/${taxSessionId}/role/${role}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(response.status, body, `Failed to create ${role}`),
    );
  }

  const person = body as PersonResponse;

  if (!Number.isInteger(person?.personId) || person.personId <= 0) {
    console.error(`Invalid ${role} creation response:`, body);

    throw new Error(`${role} was created, but no valid personId was returned`);
  }

  return person;
}

export async function updatePerson(
  personId: number,
  applicantData: ApplicantPayload,
): Promise<void> {
  if (!Number.isInteger(personId) || personId <= 0) {
    throw new Error(`Cannot update applicant: invalid personId ${personId}`);
  }

  const response = await fetch(`${API_BASE_URL}/api/persons/${personId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(applicantData),
  });

  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(
        response.status,
        body,
        `Failed to update person ${personId}`,
      ),
    );
  }
}

export async function saveCraQuestions(
  personId: number,
  craQuestions: CraQuestionPayload,
): Promise<void> {
  if (!Number.isInteger(personId) || personId <= 0) {
    throw new Error(`Cannot save CRA questions: invalid personId ${personId}`);
  }

  const url = `${API_BASE_URL}/api/cra-questions/person/${personId}`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(craQuestions),
  });

  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(
        response.status,
        body,
        `Failed to save CRA questions for person ${personId}`,
      ),
    );
  }
}

export async function savePersonCheckboxes(
  personId: number,
  checkboxData: PersonCheckboxPayload,
): Promise<void> {
  if (!Number.isInteger(personId) || personId <= 0) {
    throw new Error(`Cannot save checkbox data: invalid personId ${personId}`);
  }

  const url = `${API_BASE_URL}/api/person-checkboxes/person/${personId}`;

  console.log("Saving person checkboxes:", {
    personId,
    url,
    checkboxData,
  });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(checkboxData),
  });

  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(
        response.status,
        body,
        `Failed to save checkbox data for person ${personId}`,
      ),
    );
  }
}

export async function submitContactInfo(
  taxSessionId: number,
  contactInfo: ContactInfoPayload,
): Promise<unknown> {
  if (!Number.isInteger(taxSessionId) || taxSessionId <= 0) {
    throw new Error(
      `Cannot submit contact information: invalid taxSessionId ${taxSessionId}`,
    );
  }

  const email = contactInfo.email?.trim();
  const phone = contactInfo.phone?.trim();

  if (!email) {
    throw new Error("Contact email is required");
  }

  const payload: ContactInfoPayload = {
    email,
    phone: phone || undefined,
  };

  const url = `${API_BASE_URL}/api/tax-sessions/session/${taxSessionId}`;

  console.log("Submitting contact/email request:", {
    taxSessionId,
    url,
    payload,
  });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  // Consume the response body only once.
  const body = await readResponseBody(response);

  if (!response.ok) {
    throw new Error(
      getResponseError(
        response.status,
        body,
        "Contact/email submission failed",
      ),
    );
  }

  return body;
}
